import Link from 'next/link';
import { NextSeo } from 'next-seo';

import {
  DocsArticleLayout,
  DocsCallout,
  DocsCard,
  DocsCardGrid,
  DocsStep,
  DocsSteps,
  DocsTable,
} from '@/components/modules/Docs';
import PageLink from '@/utils/PageLink';

const tableOfContents = [
  { id: 'how-it-works', title: '1. How lead capture works' },
  { id: 'configure', title: '2. Configuring the form' },
  { id: 'timing', title: '3. When to ask' },
  { id: 'working-leads', title: '4. Working your leads' },
  { id: 'voice-leads', title: '5. Leads from phone calls' },
  { id: 'api', title: '6. Getting leads out via API' },
  { id: 'conversion', title: '7. What actually improves conversion' },
];

const LeadCapturePage = () => {
  return (
    <>
      <NextSeo
        title="Lead Capture"
        description="Collect visitor contact details inside the chat, choose which fields to ask for and when, then export leads or pull them out through the API."
        canonical="https://chat.co/docs/lead-capture"
        openGraph={{
          url: 'https://chat.co/docs/lead-capture',
          title: 'Lead Capture | Chat.co',
          description:
            'Collect visitor contact details inside the chat, choose which fields to ask for and when, then export or sync them.',
          type: 'article',
          article: {
            publishedTime: '2026-08-30T00:00:00Z',
            modifiedTime: '2026-08-30T00:00:00Z',
            section: 'Operations',
            tags: ['leads', 'lead capture', 'crm', 'conversion'],
          },
        }}
        twitter={{ cardType: 'summary_large_image' }}
      />

      <DocsArticleLayout
        category="Operations"
        categoryColor="text-blue-700"
        title="Lead capture"
        description="Turn a conversation into a contact — without turning the chat into a form nobody fills in."
        readingTime="7 min read"
        lastUpdated="August 2026"
        tableOfContents={tableOfContents}
        prevArticle={{
          title: 'Human handoff',
          href: PageLink.docsHumanHandoff,
        }}
        nextArticle={{
          title: 'Analytics, ROI & knowledge gaps',
          href: PageLink.docsAnalyticsRoi,
        }}
        keyTakeaways={[
          'The lead form is configured under Appearance → Lead Capture, per bot',
          'Four fields are available — name, email, phone, message — each independently visible and required',
          'You choose how many messages must pass before the form appears',
          'Captured leads land in the bot’s Leads screen and export to CSV',
          'Voice-line callers can leave a message, which arrives as a lead tagged to that call',
        ]}>
        <p className="lead">
          Lead capture is where the temptation to over-ask is strongest and most expensive. Every
          field you add reduces completion; every field you skip reduces what you can do with the
          result. This page covers the mechanics, and then the judgement.
        </p>

        <section id="how-it-works" className="scroll-mt-28">
          <h2>1. How lead capture works</h2>
          <p>
            When lead capture is on, the chat presents a short form inside the conversation after a
            number of messages you choose. What the visitor submits becomes a lead attached to that
            bot, visible under <strong>Leads</strong> in the sidebar.
          </p>
          <p>
            It is a form in the flow of a conversation, not a gate in front of it. The visitor has
            already asked something and got an answer before they are asked for anything — which is
            the whole reason it converts better than a modal on page load.
          </p>
        </section>

        <section id="configure" className="scroll-mt-28">
          <h2>2. Configuring the form</h2>
          <p>
            Open <strong>Appearance</strong> and go to the <strong>Lead Capture</strong> step. Turn
            it on, then decide what to ask for.
          </p>

          <DocsTable
            headers={['Field', 'Good for', 'Cost']}
            rows={[
              [
                <strong key="n">Name</strong>,
                'Personalising the follow-up',
                'Low — most people give it without thinking',
              ],
              [
                <strong key="e">Email</strong>,
                'Actually being able to reply',
                'Low, and non-negotiable if you intend to follow up at all',
              ],
              [
                <strong key="p">Phone</strong>,
                'Sales motions where a call is the next step',
                'High — this is the field that stops people',
              ],
              [
                <strong key="m">Message</strong>,
                'Context beyond what the chat already captured',
                'Medium, and often redundant — you have the transcript',
              ],
            ]}
          />

          <p>
            Each field is independently <strong>visible</strong> and <strong>required</strong>, so
            you can show phone as optional rather than dropping it entirely. You also control the
            form title, per-field placeholder text, the submit button colour, and the thank-you
            message shown afterwards.
          </p>

          <DocsCallout variant="tip" title="Start with email required, everything else optional">
            Name and email, email required. That is the configuration that converts. Add phone only
            if a human is genuinely going to call — asking for a number you never dial costs you
            leads for nothing.
          </DocsCallout>
        </section>

        <section id="timing" className="scroll-mt-28">
          <h2>3. When to ask</h2>
          <p>
            The <strong>show after N messages</strong> setting is the most consequential control on
            the page, and the default of asking early is rarely the best choice.
          </p>

          <DocsTable
            headers={['Setting', 'Effect']}
            rows={[
              [
                'After 1 message',
                'Maximum reach, lowest quality. The visitor has had one answer and does not yet know whether you are useful.',
              ],
              [
                'After 2–3 messages',
                'The sweet spot for most bots. The visitor has got real value and is engaged enough to reciprocate.',
              ],
              [
                'After 5+ messages',
                'High intent, low volume. Suits high-value B2B where you would rather have ten good leads than a hundred addresses.',
              ],
            ]}
          />

          <DocsCallout variant="note" title="Value first, then the ask">
            The whole argument for in-chat capture over a pop-up is that the visitor has already
            been helped. Asking on message one throws that advantage away and reproduces the pop-up
            experience inside a nicer container.
          </DocsCallout>
        </section>

        <section id="working-leads" className="scroll-mt-28">
          <h2>4. Working your leads</h2>
          <p>
            <strong>Leads</strong> under Interactions lists everything the bot has captured. From
            there you can:
          </p>

          <DocsCardGrid columns={3}>
            <DocsCard icon="tabler:eye" title="Open a lead">
              See what they submitted alongside the conversation that produced it.
            </DocsCard>
            <DocsCard icon="tabler:file-export" title="Export to CSV">
              Select rows and export. The route into any CRM that takes a file.
            </DocsCard>
            <DocsCard icon="tabler:mail-forward" title="Send to your team">
              Forward leads to email addresses so follow-up starts without anyone logging in.
            </DocsCard>
          </DocsCardGrid>

          <DocsCallout variant="tip" title="Read the transcript before you reply">
            The single highest-leverage habit here. A follow-up that references what the person
            actually asked lands very differently from a templated &ldquo;thanks for your
            interest&rdquo;, and the transcript is right there.
          </DocsCallout>
        </section>

        <section id="voice-leads" className="scroll-mt-28">
          <h2>5. Leads from phone calls</h2>
          <p>
            If the bot has a <Link href={PageLink.docsVoiceLines}>voice line</Link>, a caller can
            leave a message and it arrives in the same Leads list — tagged with its source so you
            can tell phone leads from chat leads, and linked back to the call it came from.
          </p>
          <p>
            One follow-up queue for both channels. Worth knowing before you build a separate process
            for voicemail.
          </p>
        </section>

        <section id="api" className="scroll-mt-28">
          <h2>6. Getting leads out via API</h2>
          <p>
            For a real CRM sync rather than a CSV, read leads programmatically. A secret key with
            the <code>analytics:read</code> scope can list the leads for a chatbot:
          </p>

          <p>
            <code>GET /client/v1/chatbots/:chatbotId/leads</code>
          </p>

          <p>
            Full request and response detail, along with authentication and scopes, is in the{' '}
            <Link href={PageLink.docsApiReference}>API reference</Link>.
          </p>

          <DocsCallout variant="warning" title="A publishable key cannot read leads">
            <code>pk_live_</code> keys are permanently limited to chat scopes. Lead data needs a
            server-side <code>sk_live_</code> key — which means the sync runs on your backend, never
            in the browser.
          </DocsCallout>
        </section>

        <section id="conversion" className="scroll-mt-28">
          <h2>7. What actually improves conversion</h2>

          <DocsSteps>
            <DocsStep title="Cut a field">
              <p>
                The reliable win, and the one people resist. Every removed field measurably raises
                completion. Ask what you will actually use this week.
              </p>
            </DocsStep>
            <DocsStep title="Say why you are asking">
              <p>
                &ldquo;So we can send you the full comparison&rdquo; beats &ldquo;Enter your
                details&rdquo;. The form title is a persuasion surface, not a label.
              </p>
            </DocsStep>
            <DocsStep title="Make the thank-you do work">
              <p>
                Tell them what happens next and when. &ldquo;Thanks!&rdquo; is a wasted screen; a
                concrete promise sets expectations and reduces the chance they chase you.
              </p>
            </DocsStep>
            <DocsStep title="Move the trigger and compare">
              <p>
                Run one setting for a fortnight, then the other. Volume and quality trade off
                against each other, and which side you want depends on your funnel — not on a
                general rule.
              </p>
            </DocsStep>
          </DocsSteps>

          <DocsCardGrid columns={2}>
            <DocsCard icon="tabler:headset" title="Human handoff" href={PageLink.docsHumanHandoff}>
              The other route from conversation to contact, for people who need help now.
            </DocsCard>
            <DocsCard
              icon="tabler:chart-bar"
              title="Analytics &amp; ROI"
              href={PageLink.docsAnalyticsRoi}>
              Put a number on what the bot is producing.
            </DocsCard>
          </DocsCardGrid>
        </section>
      </DocsArticleLayout>
    </>
  );
};

export const getServerSideProps = async () => {
  return { props: {} };
};

export default LeadCapturePage;
